import { getShapeManifest } from '../../../lib/diagram/registries';
import { Logger } from '../../../utils/logger';

type ConnectionPoint = [number, number];

type ConnectionType = "parent" | "child" | "relationship";

interface GenogramConnection {
  id: string;
  from: string;
  to: string;
  type: ConnectionType;
  points: ConnectionPoint[];
}

interface FamilyPerson {
  id: string;
  name: string;
  gender: string;
  culture?: string;
  sexualOrientation?: string;
  medicalConditions?: string[];
  [key: string]: unknown;
}

interface ParentChildRelation {
  parents: string[];
  children: string[];
}

interface FamilyData {
  people: FamilyPerson[];
  marriages?: Array<[string, string]>;
  parentChild?: ParentChildRelation[];
}

interface FamilyUnit {
  id: string;
  generation: number;
  members: GenogramPersonNode[];
  children: GenogramPersonNode[];
  parents: GenogramPersonNode[];
  column: number;
  columnSpan: number;
  requiredColumns: number;
  familySide?: 'left' | 'right' | 'center';
  sideWeight?: number;
  isSingleParent?: boolean;
}

type SiblingType = "full" | "half";

interface SiblingInfo {
  sibling: GenogramPersonNode;
  sharedParents: number;
  type: SiblingType;
}

interface GenogramPersonNode extends FamilyPerson {
  x: number;
  y: number;
  generation: number;
  column: number;
  columnSpan: number;
  columnPixelWidth?: number;
  totalColumns?: number;
  parents: GenogramPersonNode[];
  children: GenogramPersonNode[];
  spouses: GenogramPersonNode[];
  siblings: GenogramPersonNode[];
  siblingInfo: SiblingInfo[];
  familyUnit: FamilyUnit | null;
  familyUnits: FamilyUnit[];
  isSingleParent?: boolean;
}

interface LayoutOptions {
  nodeWidth?: number;
  nodeHeight?: number;
  spouseGap?: number;
  siblingGap?: number;
  generationGap?: number;
  minColumnWidth?: number;
  drawingWidth?: number;
  clusteringEnabled?: boolean;
}

interface LayoutResult {
  nodes: Array<{
    id: string;
    name: string;
    gender: string;
    x: number;
    y: number;
    generation: number;
    siblings: string[];
    spouses: string[];
    culture?: string;
    sexualOrientation?: string;
    medicalConditions?: string[];
  }>;
  edges: GenogramConnection[];
}

interface RenderFamilyUnit {
  members: GenogramPersonNode[];
  children: GenogramPersonNode[];
  unit: FamilyUnit;
}

const pan: { x: number; y: number } = { x: 50, y: 50 };
const zoom = 1;
const showGrid = true;
const hoveredPerson: string | null = null;
const NODE_WIDTH = 80;
const NODE_HEIGHT = 80;

class GenogramLayoutService {
  private nodeWidth: number;
  // Note: nodeHeight retained in constructor signature previously; no longer used
  // Remove to satisfy TS strict unused checks
  private spouseGap: number;
  private siblingGap: number;
  private generationGap: number;
  private minColumnWidth: number;
  private drawingWidth: number;
  private clusteringEnabled: boolean;

  constructor(options: LayoutOptions = {}) {
    const {
      nodeWidth = NODE_WIDTH,
      spouseGap = 250,
      siblingGap = 90,
      generationGap = 250,
      minColumnWidth = 200,
      drawingWidth,
      clusteringEnabled = true,
    } = options;

    const resolvedDrawingWidth =
      drawingWidth ?? (typeof window !== 'undefined' ? window.innerWidth - 100 : 1200);

    this.nodeWidth = nodeWidth;
    this.spouseGap = spouseGap;
    this.siblingGap = siblingGap;
    this.generationGap = generationGap;
    this.minColumnWidth = minColumnWidth;
    this.drawingWidth = resolvedDrawingWidth;
    this.clusteringEnabled = clusteringEnabled;
  }

  layout(familyData: FamilyData): GenogramPersonNode[] {
    const nodes = this.buildGraph(familyData);

    // Attempt the union-based tidy layout first
    const unionLaidOut = this.layoutUsingUnions(nodes, familyData);
    if (unionLaidOut) {
      this.minimizeCrossings(nodes);
      Logger.debug('GenogramLayout', '=== UNION LAYOUT COMPLETE ===');
      return unionLaidOut;
    }

    // Fallback: previous columnar layout
    Logger.debug('GenogramLayout', "=== BOTTOM-UP LAYOUT ALGORITHM (FALLBACK) ===");
    this.buildGenerations(nodes);
    const generationUnits = this.createGenerationUnits(nodes);
    const { totalColumns, isAncestorTree } = this.calculateColumnRequirements(generationUnits);
    this.allocateColumns(generationUnits, totalColumns, isAncestorTree);
    const adjustments = this.validateAndAdjustParentChildAdjacency(generationUnits);
    if (adjustments > 0) this.resolveColumnOverlaps(generationUnits);
    this.positionNodes(generationUnits, totalColumns);
    this.repackMarriedCouplesByGeneration(nodes, familyData);
    this.recenterChildrenGroupsUnderParents(nodes);
    this.alignGenerationHeights(nodes);
    this.resolveHorizontalOverlapsInGenerations(nodes);
    this.recenterChildrenGroupsUnderParents(nodes);
    this.minimizeCrossings(nodes);
    return Array.from(nodes.values());
  }

  // A lightweight barycenter pass to reduce long crossing parent-child edges.
  // We only reorder descendants (nodes with parents) and preserve relative order.
  private minimizeCrossings(nodes: Map<string, GenogramPersonNode>): void {
    const byGeneration = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((node) => {
      if (node.generation < 0) return;
      if (!byGeneration.has(node.generation)) byGeneration.set(node.generation, []);
      byGeneration.get(node.generation)!.push(node);
    });

    const minSpacing = this.nodeWidth + Math.max(40, Math.floor(this.siblingGap * 0.5));
    const generations = Array.from(byGeneration.keys()).sort((a, b) => a - b);

    for (const gen of generations) {
      const current = byGeneration.get(gen) ?? [];
      const descendants = current.filter((node) => node.parents.length > 0);
      if (descendants.length < 2) continue;

      const points = descendants.map((node) => {
        const targetX =
          node.parents.reduce((sum, parent) => sum + parent.x, 0) /
          Math.max(1, node.parents.length);
        return { node, targetX };
      });

      points.sort((a, b) => (a.targetX - b.targetX) || (a.node.x - b.node.x));

      const originalCenter =
        points.reduce((sum, point) => sum + point.targetX, 0) / points.length;

      let cursor = -Infinity;
      for (const point of points) {
        const nextX = Math.max(point.targetX, cursor + minSpacing);
        point.node.x = nextX;
        cursor = nextX;
      }

      const adjustedCenter =
        points.reduce((sum, point) => sum + point.node.x, 0) / points.length;
      const delta = originalCenter - adjustedCenter;
      points.forEach((point) => {
        point.node.x += delta;
      });
    }
  }

  // --- UNION-BASED TIDY LAYOUT ---
  private layoutUsingUnions(
    nodes: Map<string, GenogramPersonNode>,
    familyData: FamilyData
  ): GenogramPersonNode[] | null {
    type UnionNode = {
      id: string;
      parents: GenogramPersonNode[];
      children: GenogramPersonNode[];
      generation: number; // half-step (e.g., 0.5, 1.5)
      x: number;
      width: number;
    };

    // Build generations for persons first
    this.buildGenerations(nodes);

    const unions: UnionNode[] = [];
    const producingUnionByChild = new Map<string, UnionNode>();

    const parentChild = familyData.parentChild || [];
    parentChild.forEach((rel, idx) => {
      const parents: GenogramPersonNode[] = rel.parents
        .map((pid) => nodes.get(pid))
        .filter((p): p is GenogramPersonNode => !!p);
      const children: GenogramPersonNode[] = rel.children
        .map((cid) => nodes.get(cid))
        .filter((c): c is GenogramPersonNode => !!c);

      if (parents.length === 0 || children.length === 0) return;
      const gen = Math.min(...parents.map((p) => p.generation >= 0 ? p.generation : 0)) + 0.5;
      const union: UnionNode = {
        id: `u-${idx}`,
        parents,
        children,
        generation: gen,
        x: 0,
        width: 0,
      };
      unions.push(union);
      children.forEach((c) => producingUnionByChild.set(c.id, union));
    });

    if (unions.length === 0) return null;

    // Helper to compute Y positions
    const yForPersonGen = (g: number) => 100 + g * this.generationGap;
    // Union Y is implicit via parent and child Ys; marriage bars use those during render

    // Identify top unions: parents who do not have producing unions themselves
    const topUnions = unions.filter((u) => u.parents.every((p) => !producingUnionByChild.has(p.id)));

    // Sort top unions for stable layout
    topUnions.sort((a, b) => a.id.localeCompare(b.id));

    // Position map for persons
    const positioned = new Set<string>();

    // Place top unions left-to-right
    let cursorX = 0;
    const baseCoupleWidth = (numParents: number) =>
      numParents >= 2 ? this.nodeWidth * 2 + this.spouseGap : this.nodeWidth;

    const placeParentsAroundUnion = (u: UnionNode) => {
      const parentY = yForPersonGen(Math.max(0, Math.min(...u.parents.map((p) => p.generation >= 0 ? p.generation : 0))));
      if (u.parents.length >= 2) {
        const left = u.x - (this.nodeWidth / 2 + this.spouseGap / 2);
        const right = u.x + (this.nodeWidth / 2 + this.spouseGap / 2);
        const [p1, p2] = u.parents;
        const rowYFor = (already: GenogramPersonNode) =>
          positioned.has(already.id) ? already.y : parentY;
        if (!positioned.has(p1.id)) {
          p1.x = left;
          p1.y = rowYFor(p2);
          positioned.add(p1.id);
        }
        if (!positioned.has(p2.id)) {
          p2.x = right;
          p2.y = rowYFor(p1);
          positioned.add(p2.id);
        }
      } else {
        const p = u.parents[0];
        if (!positioned.has(p.id)) { p.x = u.x; p.y = parentY; positioned.add(p.id); }
      }
    };

    topUnions.forEach((u) => {
      u.width = baseCoupleWidth(u.parents.length);
      u.x = cursorX + u.width / 2;
      placeParentsAroundUnion(u);
      // Also place children under top unions so their X anchors to their own parents
      const children = u.children;
      if (children.length > 0) {
        const n = children.length;
        const groupWidth = n * this.nodeWidth + (n - 1) * this.siblingGap;
        const startLeft = u.x - groupWidth / 2;
        const childY = yForPersonGen(Math.max(0, Math.ceil(u.generation + 0.5)));
        children.forEach((c, idx) => {
          c.x = startLeft + idx * (this.nodeWidth + this.siblingGap) + this.nodeWidth / 2;
          c.y = childY;
          positioned.add(c.id);
        });
      }
      cursorX += u.width + this.siblingGap;
    });

    // Process remaining unions when at least one parent is positioned (spouse can be placed here)
    const placedUnion = new Set<string>(topUnions.map((u) => u.id));
    let progress = true;

    const avgXOfPositionedParents = (u: typeof unions[0]) => {
      const placedPs = u.parents.filter((p) => positioned.has(p.id));
      if (!placedPs.length) {
        return 0;
      }
      return placedPs.reduce((s, p) => s + p.x, 0) / placedPs.length;
    };

    const resolveUnionCenterX = (u: typeof unions[0]) => {
      const placedPs = u.parents.filter((p) => positioned.has(p.id));
      if (u.parents.length >= 2 && placedPs.length === 1) {
        const anchor = placedPs[0];
        return anchor.x + (this.nodeWidth + this.spouseGap) / 2;
      }
      return u.parents.reduce((s, p) => s + p.x, 0) / u.parents.length;
    };

    while (progress) {
      progress = false;
      const pending = unions.filter((u) => !placedUnion.has(u.id));
      const ready = pending.filter((u) => u.parents.some((p) => positioned.has(p.id)));
      ready.sort((a, b) => {
        const ax = avgXOfPositionedParents(a);
        const bx = avgXOfPositionedParents(b);
        return ax - bx;
      });

      ready.forEach((u) => {
        u.width = baseCoupleWidth(u.parents.length);
        u.x = resolveUnionCenterX(u);
        placeParentsAroundUnion(u);
        placedUnion.add(u.id);
        progress = true;

        const children = u.children;
        const n = children.length;
        const groupWidth = n * this.nodeWidth + (n - 1) * this.siblingGap;
        const startLeft = u.x - groupWidth / 2;
        const childY = yForPersonGen(Math.max(0, Math.ceil(u.generation + 0.5)));
        children.forEach((c, idx) => {
          c.x = startLeft + idx * (this.nodeWidth + this.siblingGap) + this.nodeWidth / 2;
          c.y = childY;
          positioned.add(c.id);
        });
      });
    }

    (familyData.marriages || []).forEach(([id1, id2]) => {
      const a = nodes.get(id1);
      const b = nodes.get(id2);
      if (!a || !b) {
        return;
      }
      const aDone = positioned.has(a.id);
      const bDone = positioned.has(b.id);
      if (aDone && !bDone) {
        b.x = a.x + this.nodeWidth + this.spouseGap;
        b.y = a.y;
        positioned.add(b.id);
      } else if (bDone && !aDone) {
        a.x = b.x - this.nodeWidth - this.spouseGap;
        a.y = b.y;
        positioned.add(a.id);
      }
    });

    this.repackMarriedCouplesByGeneration(nodes, familyData);
    this.recenterChildrenGroupsUnderParents(nodes);
    this.alignGenerationHeights(nodes);
    this.resolveHorizontalOverlapsInGenerations(nodes);
    this.recenterChildrenGroupsUnderParents(nodes);

    const allPlaced = Array.from(nodes.values()).every((n) => positioned.has(n.id));
    if (!allPlaced) return null;

    return Array.from(nodes.values());
  }

  private recenterChildrenGroupsUnderParents(
    nodes: Map<string, GenogramPersonNode>
  ): void {
    const parentSetKey = (n: GenogramPersonNode): string => {
      if (n.parents.length >= 2) {
        return [n.parents[0].id, n.parents[1].id].sort().join("::");
      }
      return n.parents.length === 1 ? n.parents[0].id : "";
    };

    const byGen = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((n) => {
      if (!byGen.has(n.generation)) {
        byGen.set(n.generation, []);
      }
      byGen.get(n.generation)!.push(n);
    });
    const gens = Array.from(byGen.keys()).sort((a, b) => a - b);
    gens.forEach((g) => {
      const level = byGen.get(g)!;
      const withParents = level.filter((n) => n.parents.length > 0);
      const groups = new Map<string, GenogramPersonNode[]>();
      withParents.forEach((n) => {
        const key = parentSetKey(n);
        if (!key) {
          return;
        }
        const arr = groups.get(key) ?? [];
        arr.push(n);
        groups.set(key, arr);
      });
      groups.forEach((group) => {
        if (group.length === 0) {
          return;
        }
        const anchor = group[0];
        const targetX =
          anchor.parents.length >= 2
            ? (anchor.parents[0].x + anchor.parents[1].x) / 2
            : anchor.parents[0].x;
        if (group.length === 1) {
          group[0].x = targetX;
          return;
        }
        group.sort((a, b) => a.x - b.x);
        const centroid =
          group.reduce((sum, n) => sum + n.x, 0) / group.length;
        const delta = targetX - centroid;
        group.forEach((n) => {
          n.x += delta;
        });
      });
    });
  }

  private alignGenerationHeights(nodes: Map<string, GenogramPersonNode>): void {
    const byGen = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((n) => {
      if (n.generation < 0) {
        return;
      }
      if (!byGen.has(n.generation)) {
        byGen.set(n.generation, []);
      }
      byGen.get(n.generation)!.push(n);
    });
    const baseY = 100;
    for (const gen of Array.from(byGen.keys()).sort((a, b) => a - b)) {
      const y = baseY + gen * this.generationGap;
      byGen.get(gen)!.forEach((n) => {
        n.y = y;
      });
    }
  }

  private resolveHorizontalOverlapsInGenerations(
    nodes: Map<string, GenogramPersonNode>
  ): void {
    const minCenterDist =
      this.nodeWidth + Math.max(20, Math.round(this.siblingGap * 0.45));
    const byGen = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((n) => {
      if (n.generation < 0) {
        return;
      }
      if (!byGen.has(n.generation)) {
        byGen.set(n.generation, []);
      }
      byGen.get(n.generation)!.push(n);
    });
    for (const gen of Array.from(byGen.keys()).sort((a, b) => a - b)) {
      const row = byGen.get(gen)!;
      if (row.length < 2) {
        continue;
      }
      const sorted = row.slice().sort((a, b) => a.x - b.x);
      let prevX = sorted[0].x;
      for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        const minX = prevX + minCenterDist;
        if (cur.x < minX) {
          cur.x = minX;
        }
        prevX = cur.x;
      }
    }
  }

  private repackMarriedCouplesByGeneration(
    nodes: Map<string, GenogramPersonNode>,
    familyData: FamilyData
  ): void {
    const byGen = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((n) => {
      if (n.generation < 0) {
        return;
      }
      if (!byGen.has(n.generation)) {
        byGen.set(n.generation, []);
      }
      byGen.get(n.generation)!.push(n);
    });

    const pairKey = (a: string, b: string) => [a, b].sort().join("::");

    for (const gen of Array.from(byGen.keys()).sort((a, b) => a - b)) {
      const level = byGen.get(gen)!;
      if (level.length === 0) {
        continue;
      }

      const beforeMin = Math.min(...level.map((n) => n.x));

      const pairs: Array<[GenogramPersonNode, GenogramPersonNode]> = [];
      const seenPair = new Set<string>();
      for (const [id1, id2] of familyData.marriages || []) {
        const a = nodes.get(id1);
        const b = nodes.get(id2);
        if (!a || !b) {
          continue;
        }
        if (a.generation !== gen || b.generation !== gen) {
          continue;
        }
        const pk = pairKey(id1, id2);
        if (seenPair.has(pk)) {
          continue;
        }
        seenPair.add(pk);
        pairs.push([a, b]);
      }

      if (pairs.length === 0) {
        continue;
      }

      const pairedIds = new Set<string>();
      pairs.forEach(([a, b]) => {
        pairedIds.add(a.id);
        pairedIds.add(b.id);
      });
      const singles = level.filter((n) => !pairedIds.has(n.id));
      pairs.sort(
        (a, b) => (a[0].x + a[1].x) / 2 - (b[0].x + b[1].x) / 2
      );

      const betweenCouples = Math.max(40, Math.round(this.siblingGap * 0.62));

      let cursor = Math.min(pairs[0][0].x, pairs[0][1].x);

      for (const [p, q] of pairs) {
        const left = p.x <= q.x ? p : q;
        const right = p.x <= q.x ? q : p;
        left.x = cursor;
        right.x = cursor + this.nodeWidth + this.spouseGap;
        cursor = right.x + this.nodeWidth + betweenCouples;
      }

      singles.sort((a, b) => a.x - b.x);
      for (const s of singles) {
        s.x = cursor + this.nodeWidth / 2;
        cursor += this.nodeWidth + this.siblingGap;
      }

      const afterMin = Math.min(...level.map((n) => n.x));
      const shift = beforeMin - afterMin;
      if (shift !== 0) {
        level.forEach((n) => {
          n.x += shift;
        });
      }
    }
  }

  private buildGraph(
    familyData: FamilyData
  ): Map<string, GenogramPersonNode> {
    const nodes = new Map<string, GenogramPersonNode>();

    // Create all nodes
    familyData.people.forEach((person) => {
      const node: GenogramPersonNode = {
        ...person,
        x: 0,
        y: 0,
        generation: -1,
        column: -1,
        columnSpan: 1,
        parents: [],
        children: [],
        spouses: [],
        siblings: [],
        siblingInfo: [],
        familyUnit: null,
        familyUnits: [],
      };
      nodes.set(person.id, node);
    });

    // Add marriages
    (familyData.marriages || []).forEach(([id1, id2]) => {
      const p1 = nodes.get(id1);
      const p2 = nodes.get(id2);
      if (p1 && p2) {
        p1.spouses.push(p2);
        p2.spouses.push(p1);
      }
    });

    // Add parent-child relationships
    (familyData.parentChild || []).forEach(({ parents, children }) => {
      parents.forEach((parentId) => {
        const parent = nodes.get(parentId);
        if (parent) {
          children.forEach((childId) => {
            const child = nodes.get(childId);
            if (child) {
              parent.children.push(child);
              child.parents.push(parent);
            }
          });
        }
      });
    });

    // Identify siblings (including half-siblings)
    Logger.debug("GenogramLayout", "  Detecting siblings (including half-siblings):");
    nodes.forEach((node) => {
      const siblingSet = new Set<GenogramPersonNode>();

      // For each parent, collect all their children
      node.parents.forEach((parent) => {
        parent.children.forEach((child) => {
          if (child.id !== node.id) {
            siblingSet.add(child);
          }
        });
      });

      // Convert set to array for the siblings property
      node.siblings = Array.from(siblingSet) as GenogramPersonNode[];

      // Also track whether siblings are full or half siblings
      node.siblingInfo = [];
      siblingSet.forEach((sibling) => {
        // Count shared parents
        const sharedParents = node.parents.filter((p) =>
          sibling.parents.includes(p)
        );
        const siblingType =
          sharedParents.length === node.parents.length &&
          sharedParents.length === sibling.parents.length
            ? "full"
            : "half";

        node.siblingInfo.push({
          sibling: sibling,
          sharedParents: sharedParents.length,
          type: siblingType,
        });
      });

      if (node.siblingInfo.length > 0) {
        const siblingDetails = node.siblingInfo
          .map((info) => `${info.sibling.name}(${info.type})`)
          .join(", ");
        Logger.debug("GenogramLayout", `    ${node.name} has siblings: ${siblingDetails}`);
      }
    });

    return nodes;
  }

  private buildGenerations(nodes: Map<string, GenogramPersonNode>): void {
    // Step 1: Assign generations based on parent-child relationships only
    const roots = Array.from(nodes.values()).filter(
      (n) => n.parents.length === 0
    );

    // BFS to assign generations based on ancestry
    const queue: GenogramPersonNode[] = [...roots];
    const visited = new Set<string>();

    // Initialize roots as generation 0
    roots.forEach((root) => {
      root.generation = root.children.length === 0 ? -1 : 0;
      visited.add(root.id);
    });

    // Process queue for parent-child relationships
    let currentIndex = 0;
    while (currentIndex < queue.length) {
      const node = queue[currentIndex++];

      // Process all children
      node.children.forEach((child) => {
        if (!visited.has(child.id)) {
          // Child's generation is parent's generation + 1
          child.generation = node.generation + 1;
          visited.add(child.id);
          queue.push(child);
        } else if (child.generation <= node.generation) {
          // Fix generation if needed (child must be below parent)
          child.generation = node.generation + 1;
        }
      });
    }

    // Step 2: Fix spouses to be in same generation
    nodes.forEach((node) => {
      if (node.generation >= 0 && node.spouses.length > 0) {
        node.spouses.forEach((spouse) => {
          if (spouse.generation === -1) {
            // Spouse not yet assigned - put in same generation
            spouse.generation = node.generation;
          } else if (spouse.generation !== node.generation) {
            // Spouses in different generations - align them
            // Use the generation that makes most sense based on their children
            if (node.children.length > 0 || spouse.children.length > 0) {
              // If they have children, use generation that's one above children
              const childGens = [...node.children, ...spouse.children]
                .map((c) => c.generation)
                .filter((g) => g >= 0);

              if (childGens.length > 0) {
                const childGen = Math.min(...childGens);
                const correctGen = childGen - 1;
                node.generation = correctGen;
                spouse.generation = correctGen;
              }
            } else {
              // No children - use the earlier generation
              const targetGen = Math.min(node.generation, spouse.generation);
              node.generation = targetGen;
              spouse.generation = targetGen;
            }
          }
        });
      }
    });
    // Step 3: Verify and log generation assignments
    const genMap = new Map<number, string[]>();
    nodes.forEach((node) => {
      if (node.generation >= 0) {
        if (!genMap.has(node.generation)) {
          genMap.set(node.generation, []);
        }
        genMap.get(node.generation)!.push(node.name);
      }
    });

    // Sort and log
    const sortedGens = Array.from(genMap.keys()).sort((a, b) => a - b);
    sortedGens.forEach((gen) => {
      Logger.debug("GenogramLayout", `  Generation ${gen}: ${genMap.get(gen)!.join(", ")}`);
    });
  }

  private createGenerationUnits(
    nodes: Map<string, GenogramPersonNode>
  ): Map<number, FamilyUnit[]> {
    const generations = new Map<number, GenogramPersonNode[]>();

    // Group nodes by generation
    nodes.forEach((node) => {
      if (!generations.has(node.generation)) {
        generations.set(node.generation, []);
      }
      generations.get(node.generation)!.push(node);
    });

    // Create family units for each generation
    const generationUnits = new Map<number, FamilyUnit[]>();

    generations.forEach((genNodes, gen) => {
      const units: FamilyUnit[] = [];
      const processed = new Set<string>();

      // First pass: create units for married couples
      const marriageUnits = new Map<string, FamilyUnit>();
      genNodes.forEach((person) => {
        if (processed.has(person.id)) return;

        person.spouses.forEach((spouse) => {
          if (spouse.generation === gen && !processed.has(spouse.id)) {
            // Create a unique marriage key (sorted to avoid duplicates)
            const marriageKey = [person.id, spouse.id].sort().join("-");

            if (!marriageUnits.has(marriageKey)) {
              const unitId = `marriage-${marriageKey}`;
              const unit: FamilyUnit = {
                id: unitId,
                generation: gen,
                members: [person, spouse],
                children: [],
                parents: [],
                column: -1,
                columnSpan: 1,
                requiredColumns: 2,
              };

              // Mark both as processed for this marriage
              processed.add(person.id);
              processed.add(spouse.id);

              // Assign this specific marriage unit to both members
              if (!person.familyUnits) person.familyUnits = [];
              if (!spouse.familyUnits) spouse.familyUnits = [];
              person.familyUnits.push(unit);
              spouse.familyUnits.push(unit);

              // For backward compatibility, set familyUnit to the first marriage
              if (!person.familyUnit) person.familyUnit = unit;
              if (!spouse.familyUnit) spouse.familyUnit = unit;

              // Collect children that belong to this specific marriage
              const sharedChildren = person.children.filter((child) =>
                spouse.children.includes(child)
              );
              unit.children = sharedChildren;

              // Collect unique parents
              const parentsSet = new Set<GenogramPersonNode>();
              [person, spouse].forEach((member) => {
                member.parents.forEach((parent) => parentsSet.add(parent));
              });
              unit.parents = Array.from(parentsSet);

              marriageUnits.set(marriageKey, unit);
              units.push(unit);
            }
          }
        });
      });

      // Second pass: group remaining siblings who share parents
      const siblingGroups = new Map<string, GenogramPersonNode[]>();
      genNodes.forEach((person) => {
        if (processed.has(person.id)) return;

        // Create a key based on parent IDs
        const parentKey = person.parents
          .map((p) => p.id)
          .sort()
          .join("-");

        if (parentKey && person.parents.length > 0) {
          if (!siblingGroups.has(parentKey)) {
            siblingGroups.set(parentKey, []);
          }
          siblingGroups.get(parentKey)!.push(person);
        }
      });

      // Create units for sibling groups
      siblingGroups.forEach((siblings) => {
        if (siblings.length > 1) {
          // Multiple siblings - group them together
          const unit: FamilyUnit = {
            id: siblings.map((s) => s.id).join("-"),
            generation: gen,
            members: siblings,
            children: [], // Don't add children here - siblings shouldn't have shared children unless they're married
            parents: siblings[0].parents, // All siblings share same parents
            column: -1,
            columnSpan: 1,
            requiredColumns: 1,
          };

          siblings.forEach((sibling) => {
            sibling.familyUnit = unit;
            processed.add(sibling.id);
            // Don't add children from siblings to the sibling unit
            // Each sibling will have their own marriage unit with their specific children
          });

          units.push(unit);
        }
      });

      // Third pass: create individual units for any remaining unprocessed nodes
      genNodes.forEach((person) => {
        if (!processed.has(person.id)) {
          const unit: FamilyUnit = {
            id: person.id,
            generation: gen,
            members: [person],
            children: person.children || [],
            parents: person.parents || [],
            column: -1,
            columnSpan: 1,
            requiredColumns: 1,
            isSingleParent: person.children.length > 0, // Mark if this is a single parent with children
          };

          person.familyUnit = unit;
          processed.add(person.id);
          units.push(unit);
        }
      });

      Logger.debug("GenogramLayout", `  Generation ${gen}: Created ${units.length} family units`);
      units.forEach((unit) => {
        const unitType =
          unit.members.length === 2 &&
          unit.members[0].spouses.includes(unit.members[1])
            ? "couple"
            : unit.members.length > 1
            ? "siblings"
            : "individual";
        Logger.debug("GenogramLayout", 
          `    Unit: ${unit.members
            .map((m) => m.name)
            .join(", ")} (${unitType})`
        );
      });

      generationUnits.set(gen, units);
    });

    return generationUnits;
  }

  // Resolve column overlaps within each generation
  private resolveColumnOverlaps(
    generationUnits: Map<number, FamilyUnit[]>
  ): void {
    const maxGen = Math.max(...Array.from(generationUnits.keys()));

    for (let gen = 0; gen <= maxGen; gen++) {
      const units = generationUnits.get(gen) || [];
      if (units.length <= 1) continue;

      // Sort units by column
      units.sort((a, b) => a.column - b.column);

      // Check for overlaps and adjust
      let overlapsFixed = 0;
      for (let i = 1; i < units.length; i++) {
        const prevUnit = units[i - 1];
        const currUnit = units[i];

        const prevEnd = prevUnit.column + prevUnit.columnSpan;
        const currStart = currUnit.column;

        // If there's an overlap
        if (currStart < prevEnd) {
          // const overlap = prevEnd - currStart;
          currUnit.column = prevEnd; // Move current unit to end of previous
          overlapsFixed++;

          Logger.debug("GenogramLayout", 
            `    Fixed overlap in gen ${gen}: moved ${currUnit.members.map(m => m.name).join('+')} ` +
            `from column ${currStart} to ${currUnit.column}`
          );

          // Recursively check if this created a new overlap with the next unit
          if (i < units.length - 1) {
            i--; // Recheck this position
          }
        }
      }

      if (overlapsFixed > 0) {
        Logger.debug("GenogramLayout", `  Generation ${gen}: Fixed ${overlapsFixed} overlaps`);
      }
    }
  }

  // Validate and enforce parent-child column adjacency
  private validateAndAdjustParentChildAdjacency(
    generationUnits: Map<number, FamilyUnit[]>
  ): number {
    Logger.debug("GenogramLayout", "\n  Validating parent-child column adjacency:");

    const maxGen = Math.max(...Array.from(generationUnits.keys()));
    let adjustmentsMade = 0;

    // Check each generation's relationship with the previous one
    for (let gen = 1; gen <= maxGen; gen++) {
      const units = generationUnits.get(gen) || [];

      units.forEach(unit => {
        // For married couples, check EACH member's adjacency to their own parents
        const isCouple = unit.members.length === 2 &&
                         unit.members[0].spouses.includes(unit.members[1]);

        if (isCouple) {
          // Check each spouse's adjacency to their own parents
          unit.members.forEach((member) => {
            if (member.parents.length === 0) return;

            const memberParentUnits = new Set<FamilyUnit>();
            member.parents.forEach(parent => {
              if (parent.familyUnit) {
                memberParentUnits.add(parent.familyUnit);
              }
            });

            if (memberParentUnits.size === 0) return;

            // Get this member's parent column range
            let minParentCol = Infinity;
            let maxParentCol = -Infinity;

            memberParentUnits.forEach(parentUnit => {
              minParentCol = Math.min(minParentCol, parentUnit.column);
              maxParentCol = Math.max(maxParentCol, parentUnit.column + parentUnit.columnSpan - 1);
            });

            // Check if couple's columns are adjacent to this member's parents
            const childMinCol = unit.column;
            const childMaxCol = unit.column + unit.columnSpan - 1;

            // Tight adjacency: maximum 1 column gap
            const isAdjacent =
              (childMaxCol >= minParentCol - 1 && childMinCol <= maxParentCol + 1);

            if (!isAdjacent) {
              Logger.debug("GenogramLayout", 
                `    WARNING: ${unit.members.map(m => m.name).join('+')} ` +
                `(cols ${childMinCol}-${childMaxCol}) is NOT adjacent to ${member.name}'s parents ` +
                `(cols ${minParentCol}-${maxParentCol})`
              );
              // Adjust couple to midpoint of combined parent range
              const overallMin = Math.min(minParentCol, childMinCol);
              const overallMax = Math.max(maxParentCol, childMaxCol);
              const center = Math.floor((overallMin + overallMax) / 2);
              const childSpan = unit.columnSpan;
              unit.column = Math.max(minParentCol, Math.min(maxParentCol - childSpan + 1, center));
              adjustmentsMade++;
              Logger.debug("GenogramLayout", `    ADJUSTED couple to column ${unit.column}`);
            }
          });
        } else {
          // For non-couples, check standard parent adjacency (covers single parents and sibling groups)
          if (unit.parents.length === 0) return;

          const parentUnits = new Set<FamilyUnit>();
          unit.members.forEach(member => {
            member.parents.forEach(parent => {
              if (parent.familyUnit) {
                parentUnits.add(parent.familyUnit);
              }
            });
          });

          if (parentUnits.size === 0) return;

          // Get parent column range
          let minParentCol = Infinity;
          let maxParentCol = -Infinity;

          parentUnits.forEach(parentUnit => {
            minParentCol = Math.min(minParentCol, parentUnit.column);
            maxParentCol = Math.max(maxParentCol, parentUnit.column + parentUnit.columnSpan - 1);
          });

          // Check if child columns are directly under parent range
          const childMinCol = unit.column;
          const childMaxCol = unit.column + unit.columnSpan - 1;

          // Strict adjacency: children should be within or immediately adjacent to parent columns
          const isWithinRange = childMinCol >= minParentCol && childMaxCol <= maxParentCol;
          const isAdjacent =
            isWithinRange ||
            (childMaxCol >= minParentCol - 1 && childMinCol <= maxParentCol + 1);

          if (!isAdjacent) {
            Logger.debug("GenogramLayout", 
              `    WARNING: ${unit.members.map(m => m.name).join('+')} ` +
              `(cols ${childMinCol}-${childMaxCol}) is NOT adjacent to parents ` +
              `(cols ${minParentCol}-${maxParentCol})`
            );
            adjustmentsMade++;

            // Position child directly under parents center; for single parent, min==max
            const parentCenter = (minParentCol + maxParentCol) / 2;
            const childSpan = unit.columnSpan;
            unit.column = Math.max(minParentCol, Math.min(maxParentCol - childSpan + 1, Math.floor(parentCenter - childSpan / 2)));

            Logger.debug("GenogramLayout", `    ADJUSTED to column ${unit.column}`);
          }
        }
      });
    }

    Logger.debug("GenogramLayout", `  Total adjustments made: ${adjustmentsMade}`);
    return adjustmentsMade;
  }

  // Assign family sides (paternal/maternal) to keep families clustered
  private assignFamilySides(
    generationUnits: Map<number, FamilyUnit[]>,
    isAncestorTree: boolean
  ): void {
    if (!this.clusteringEnabled) return;

    Logger.debug("GenogramLayout", "\n  Assigning family sides for clustering:");

    if (isAncestorTree) {
      // For ancestor trees, assign left/right based on parent lineage
      const maxGen = Math.max(...Array.from(generationUnits.keys()));

      // Start from top generation (gen 0) and assign sides
      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen) || [];

        units.forEach((unit, idx) => {
          if (gen === 0) {
            // Top generation: assign left or right based on position
            unit.familySide = idx < units.length / 2 ? 'left' : 'right';
            unit.sideWeight = idx; // Lower = more left
            Logger.debug("GenogramLayout", `    ${unit.members.map(m => m.name).join('+')} assigned to ${unit.familySide} side`);
          } else {
            // Lower generations: inherit from parents
            const parentSides = new Set<FamilyUnit['familySide']>();
            const parentWeights: number[] = [];

            unit.parents.forEach((parent) => {
              if (parent.familyUnit && parent.familyUnit.familySide) {
                parentSides.add(parent.familyUnit.familySide);
                parentWeights.push(parent.familyUnit.sideWeight || 0);
              }
            });

            // If parents from different sides, use average weight
            if (parentSides.size === 1) {
              unit.familySide = Array.from(parentSides)[0];
            } else if (parentWeights.length > 0) {
              const avgWeight = parentWeights.reduce((a, b) => a + b, 0) / parentWeights.length;
              unit.familySide = avgWeight < units.length / 2 ? 'left' : 'right';
            } else {
              unit.familySide = 'center';
            }

            unit.sideWeight = parentWeights.length > 0
              ? parentWeights.reduce((a, b) => a + b, 0) / parentWeights.length
              : units.length / 2;

            Logger.debug("GenogramLayout", `    ${unit.members.map(m => m.name).join('+')} assigned to ${unit.familySide} side (weight: ${unit.sideWeight.toFixed(1)})`);
          }
        });
      }
    } else {
      // For descendant trees, assign based on which parent's family line
      // This helps keep paternal and maternal families on consistent sides
      const gen0Units = generationUnits.get(0) || [];

      gen0Units.forEach((unit, idx) => {
        unit.familySide = idx === 0 ? 'center' : (idx % 2 === 0 ? 'left' : 'right');
        unit.sideWeight = idx;
        Logger.debug("GenogramLayout", `    ${unit.members.map(m => m.name).join('+')} assigned to ${unit.familySide} side`);
      });

      // Propagate to descendants
      const maxGen = Math.max(...Array.from(generationUnits.keys()));
      for (let gen = 1; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen) || [];

        units.forEach((unit) => {
          const parentSides = new Set<FamilyUnit['familySide']>();
          const parentWeights: number[] = [];

          unit.parents.forEach((parent) => {
            if (parent.familyUnit) {
              if (parent.familyUnit.familySide) {
                parentSides.add(parent.familyUnit.familySide);
              }
              if (parent.familyUnit.sideWeight !== undefined) {
                parentWeights.push(parent.familyUnit.sideWeight);
              }
            }
          });

          // Children inherit parent's side
          if (parentSides.size === 1) {
            unit.familySide = Array.from(parentSides)[0];
          } else {
            unit.familySide = 'center';
          }

          unit.sideWeight = parentWeights.length > 0
            ? parentWeights.reduce((a, b) => a + b, 0) / parentWeights.length
            : 0;
        });
      }
    }
  }

  private calculateColumnRequirements(
    generationUnits: Map<number, FamilyUnit[]>
  ): { totalColumns: number; isAncestorTree: boolean } {
    // Find max generation
    const maxGen = Math.max(...Array.from(generationUnits.keys()));

    // Detect tree type by comparing top and bottom generations
    const gen0Units = generationUnits.get(0) || [];
    const maxGenUnits = generationUnits.get(maxGen) || [];

    // In ancestor trees: many units at top (gen 0), few at bottom (max gen)
    const isAncestorTree =
      gen0Units.length >= 4 && gen0Units.length >= maxGenUnits.length * 2;

    Logger.debug("GenogramLayout", `  Tree type: ${isAncestorTree ? "ANCESTOR" : "DESCENDANT"}`);
    Logger.debug("GenogramLayout", 
      `  Gen 0 units: ${gen0Units.length}, Gen ${maxGen} units: ${maxGenUnits.length}`
    );

    // Assign family sides for clustering
    this.assignFamilySides(generationUnits, isAncestorTree);

    let totalColumns = 0;

    if (isAncestorTree) {
      // For ancestor trees: Compact approach - minimal columns
      // Count unique column positions needed at the widest generation
      let maxUnitsInGen = 0;
      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen);
        if (units && units.length > maxUnitsInGen) {
          maxUnitsInGen = units.length;
        }
      }
      totalColumns = Math.max(gen0Units.length, maxUnitsInGen);

      Logger.debug("GenogramLayout", 
        `  ANCESTOR TREE: ${totalColumns} columns (gen0: ${gen0Units.length}, max: ${maxUnitsInGen})`
      );

      // All units get 1 column worth of space for compact layout
      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen);
        if (!units) continue;
        units.forEach((unit) => {
          unit.requiredColumns = 1;
        });
      }
    } else {
      // For descendant trees: Calculate bottom-up
      Logger.debug("GenogramLayout", `  DESCENDANT TREE: Calculating columns bottom-up`);

      // Use compact column allocation - each unit needs just 1 column
      for (let gen = maxGen; gen >= 0; gen--) {
        const units = generationUnits.get(gen);
        if (!units) continue;

        units.forEach((unit) => {
          // In compact mode, everyone gets 1 column
          unit.requiredColumns = 1;
        });
      }

      // Total columns is the maximum number of units in any generation
      let maxUnitsInGen = 0;
      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen);
        if (units && units.length > maxUnitsInGen) {
          maxUnitsInGen = units.length;
        }
      }
      totalColumns = maxUnitsInGen;
    }

    Logger.debug("GenogramLayout", `  Total columns needed: ${totalColumns}`);
    return { totalColumns, isAncestorTree };
  }

  private allocateColumns(
    generationUnits: Map<number, FamilyUnit[]>,
    totalColumns: number,
    isAncestorTree: boolean
  ): void {
    const maxGen = Math.max(...Array.from(generationUnits.keys()));

    Logger.debug("GenogramLayout", 
      `  Allocating columns for ${
        isAncestorTree ? "ANCESTOR" : "DESCENDANT"
      } tree with ${totalColumns} total columns`
    );

    if (isAncestorTree) {
      // ANCESTOR TREE: Top-down allocation starting from generation 0
      // Generation 0 (top) gets sequential columns
      // Each lower generation centers under their parents

      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen);
        if (!units) continue;

        Logger.debug("GenogramLayout", `  Generation ${gen}:`);

        if (gen === 0) {
          // Top generation - allocate columns with clustering consideration
          // Sort units by family side to keep families together
          const sortedUnits = this.clusteringEnabled
            ? [...units].sort((a, b) => (a.sideWeight || 0) - (b.sideWeight || 0))
            : units;

          let currentColumn = 0;
          sortedUnits.forEach((unit) => {
            unit.column = currentColumn;
            unit.columnSpan = 1;
            Logger.debug("GenogramLayout", 
              `    ${unit.members.map((m) => m.name).join("+")} gets column ${
                unit.column
              } (side: ${unit.familySide || 'unknown'})`
            );
            currentColumn += 1;
          });
        } else {
          // Lower generations - group by parent relationships
          // For each unit, find which parents it descends from
          units.forEach((unit) => {
            // Find all parent family units
            const parentUnits: FamilyUnit[] = [];
            unit.parents.forEach((parent) => {
              if (
                parent.familyUnit &&
                !parentUnits.includes(parent.familyUnit)
              ) {
                parentUnits.push(parent.familyUnit);
              }
            });

            if (parentUnits.length === 0) {
              // No parents - shouldn't happen in ancestor trees
              unit.column = 0;
              unit.columnSpan = 1;
              Logger.debug("GenogramLayout", 
                `    WARNING: ${unit.members
                  .map((m)  => m.name)
                  .join("+")} has no parent units`
              );
            } else if (parentUnits.length === 1) {
              // Single parent unit - place directly on same column
              const parentUnit: FamilyUnit = parentUnits[0];
              unit.column = parentUnit.column;
              unit.columnSpan = 1;
              Logger.debug("GenogramLayout", 
                `    ${unit.members.map((m) => m.name).join("+")} gets column ${
                  unit.column
                } (same as ${parentUnit.members.map((m) => m.name).join("+")})`
              );
            } else {
              // Multiple parent units - use minimal span to keep tight
              const minColumn = Math.min(...parentUnits.map((pu) => pu.column));
              const maxColumn = Math.max(
                ...parentUnits.map((pu) => pu.column + pu.columnSpan - 1)
              );

              // Use the center column between parents rather than spanning all
              const centerCol = Math.floor((minColumn + maxColumn) / 2);
              unit.column = centerCol;
              unit.columnSpan = 1;

              Logger.debug("GenogramLayout", 
                `    ${unit.members
                  .map((m) => m.name)
                  .join("+")} at column ${unit.column} (centered between parents at cols ${minColumn}-${maxColumn})`
              );
            }
          });
        }
      }
    } else {
      // DESCENDANT TREE: Original top-down allocation
      for (let gen = 0; gen <= maxGen; gen++) {
        const units = generationUnits.get(gen);
        if (!units) continue;

        Logger.debug("GenogramLayout", `  Generation ${gen}:`);

        if (gen === 0) {
          // Root generation - allocate columns sequentially
          // Try to keep families whose children will marry close together
          let currentColumn = 0;

          // Sort units to group families that will be connected through children's marriages
          const sortedUnits = [...units];

          // Detect if children from different families will marry
          const familyConnections = new Map<FamilyUnit, FamilyUnit[]>();
          sortedUnits.forEach((unit) => {
            const connections = new Set<FamilyUnit>();

            // Check if this unit's children marry children from other units
            unit.children.forEach((child) => {
              child.spouses.forEach((spouse) => {
                spouse.parents.forEach((spouseParent) => {
                  if (spouseParent.familyUnit && spouseParent.familyUnit !== unit) {
                    // Find which root unit this spouse's parent belongs to
                    sortedUnits.forEach((otherUnit) => {
                      if (otherUnit.members.includes(spouseParent)) {
                        connections.add(otherUnit);
                      }
                    });
                  }
                });
              });
            });

            if (connections.size > 0) {
              familyConnections.set(unit, Array.from(connections));
            }
          });

          // Place connected families adjacent to each other
          const placed = new Set<FamilyUnit>();
          const orderedUnits: FamilyUnit[] = [];

          sortedUnits.forEach((unit) => {
            if (!placed.has(unit)) {
              orderedUnits.push(unit);
              placed.add(unit);

              // Add connected families right after
              if (familyConnections.has(unit)) {
                familyConnections.get(unit)!.forEach((connectedUnit) => {
                  if (!placed.has(connectedUnit)) {
                    orderedUnits.push(connectedUnit);
                    placed.add(connectedUnit);
                  }
                });
              }
            }
          });

          orderedUnits.forEach((unit) => {
            unit.column = currentColumn;
            unit.columnSpan = 1; // Compact: each unit gets exactly 1 column
            Logger.debug("GenogramLayout", 
              `    ${unit.members.map((m) => m.name).join("+")} gets column ${unit.column}`
            );
            currentColumn += 1;
          });
        } else {
          // Child generation - allocate within parent's column space
          // Maintain a generation-wide reservation to avoid cross-parent collisions
          const globalUsedColumns = new Set<number>();
          // Group units by their parent unit
          const unitsByParent = new Map<string, FamilyUnit[]>();
          // Bridge couples are couples whose members' parents come from different family units
          const bridgeCouples: FamilyUnit[] = [];

          units.forEach((unit) => {
            // Find which parent unit this unit belongs under
            let parentUnit: FamilyUnit | null = null;

            if (unit.parents.length > 0) {
              // For children, we need to find the correct marriage unit that produced them
              // Look through all parent family units to find the one that has these specific children
              const possibleParentUnits: FamilyUnit[] = [];

              unit.parents.forEach((parent) => {
                if (parent.familyUnits) {
                  parent.familyUnits.forEach((familyUnit) => {
                    // Check if this family unit contains these specific children
                    const unitMemberIds = unit.members.map((m) => m.id);
                    const hasAllMembers = unitMemberIds.every((memberId) =>
                      familyUnit.children.some((child) => child.id === memberId)
                    );
                    if (
                      hasAllMembers &&
                      !possibleParentUnits.includes(familyUnit)
                    ) {
                      possibleParentUnits.push(familyUnit);
                    }
                  });
                }
              });

              // Use the specific marriage unit that has these children
              if (possibleParentUnits.length > 0) {
                parentUnit = possibleParentUnits[0];
              } else {
                // If this is a couple and each member's parents belong to different units, mark as bridge
                const isCouple = unit.members.length === 2 && unit.members[0].spouses.includes(unit.members[1]);
                if (isCouple) {
                  const member1ParentUnits: FamilyUnit[] = [];
                  const member2ParentUnits: FamilyUnit[] = [];
                  unit.members[0].parents.forEach(p => { if (p.familyUnit) member1ParentUnits.push(p.familyUnit); });
                  unit.members[1].parents.forEach(p => { if (p.familyUnit) member2ParentUnits.push(p.familyUnit); });
                  const distinct = new Set([
                    ...member1ParentUnits.map(u => u.id),
                    ...member2ParentUnits.map(u => u.id)
                  ]);
                  if (member1ParentUnits.length > 0 && member2ParentUnits.length > 0 && distinct.size > 1) {
                    bridgeCouples.push(unit);
                    return; // Skip assigning to a single parent group
                  }
                }

                // Fallback: For single parents or one-sided ancestry, use primary parent's unit when available
                const parent = unit.parents[0];
                parentUnit = parent?.familyUnit || null;

                if (parent && parent.familyUnits && parent.familyUnits.length > 1) {
                  Logger.debug("GenogramLayout", 
                    `    INFO: ${unit.members.map(m => m.name).join("+")} has parent with multiple marriages`
                  );
                }
              }
            }

            if (!parentUnit) {
              Logger.debug("GenogramLayout", 
                `    WARNING: No parent unit found for ${unit.members
                  .map((m) => m.name)
                  .join("+")}`
              );
              unit.column = 0;
              unit.columnSpan = unit.requiredColumns;
              return;
            }

            const parentKey = parentUnit.id;
            if (!unitsByParent.has(parentKey)) {
              unitsByParent.set(parentKey, []);
            }
            unitsByParent.get(parentKey)!.push(unit);
          });

          // Reserve columns for bridge couples first so parent-group allocation respects them
          bridgeCouples.forEach((childUnit) => {
            const m1Units: FamilyUnit[] = [];
            const m2Units: FamilyUnit[] = [];
            childUnit.members[0].parents.forEach(p => { if (p.familyUnit) m1Units.push(p.familyUnit); });
            childUnit.members[1].parents.forEach(p => { if (p.familyUnit) m2Units.push(p.familyUnit); });

            const m1Col = m1Units.length > 0 ? m1Units[0].column : null;
            const m2Col = m2Units.length > 0 ? m2Units[0].column : null;
            if (m1Col === null && m2Col === null) return;

            const leftCol = Math.min(m1Col ?? Infinity, m2Col ?? Infinity);
            const rightCol = Math.max(m1Col ?? -1, m2Col ?? -1);

            let start = Math.max(0, leftCol);
            let end = Math.max(start, rightCol);

            // Reserve the entire span between the two parent families to keep the couple bar clean
            while ([...Array(end - start + 1).keys()].some(i => globalUsedColumns.has(start + i))) {
              start++;
              end++;
            }

            childUnit.column = start;
            childUnit.columnSpan = end - start + 1;
            for (let col = start; col <= end; col++) globalUsedColumns.add(col);

            Logger.debug("GenogramLayout", `    ${childUnit.members.map(m => m.name).join('+')} (bridge) pre-reserved span ${childUnit.column}-${childUnit.column + childUnit.columnSpan - 1}`);
          });

          // Allocate columns for each parent's children (respects pre-reserved bridge slots)
          unitsByParent.forEach((childUnits, parentKey) => {
            // Find the parent unit
            const parentGen = gen - 1;
            const parentUnits = generationUnits.get(parentGen) || [];
            const parentUnit = parentUnits.find((u) => u.id === parentKey);

            if (!parentUnit) {
              Logger.debug("GenogramLayout", `    WARNING: Parent unit ${parentKey} not found`);
              return;
            }

            // Sort child units for consistent ordering
            childUnits.sort((a, b) => {
              const aFirst = a.members[0].id;
              const bFirst = b.members[0].id;
              return aFirst.localeCompare(bFirst);
            });

            // Track used columns to avoid overlaps within same parent group (in addition to global)
            const usedColumns = new Set<number>();

            // For each child unit, determine the optimal position
            // considering BOTH the parent unit AND each member's own parents
            childUnits.forEach((childUnit) => {
              // Check if this is a married couple where members have different parents
              const isCouple = childUnit.members.length === 2 &&
                               childUnit.members[0].spouses.includes(childUnit.members[1]);

              let targetColumn: number;
              const targetSpan = 1; // Compact: always 1 column

              if (isCouple) {
                // For married couples, find the parent columns for EACH spouse
                const member1ParentUnits: FamilyUnit[] = [];
                const member2ParentUnits: FamilyUnit[] = [];

                childUnit.members[0].parents.forEach(p => {
                  if (p.familyUnit) member1ParentUnits.push(p.familyUnit);
                });

                childUnit.members[1].parents.forEach(p => {
                  if (p.familyUnit) member2ParentUnits.push(p.familyUnit);
                });

                // Get parent columns
                const member1Col = member1ParentUnits.length > 0 ? member1ParentUnits[0].column : null;
                const member2Col = member2ParentUnits.length > 0 ? member2ParentUnits[0].column : null;

                // Position couple at midpoint between parents, or adjacent to one parent
                if (member1Col !== null && member2Col !== null) {
                  const avgCol = Math.round((member1Col + member2Col) / 2);
                  targetColumn = avgCol;

                  Logger.debug("GenogramLayout", 
                    `    ${childUnit.members.map(m => m.name).join('+')} at col ${targetColumn} ` +
                    `(between ${childUnit.members[0].name}'s parent col ${member1Col} and ` +
                    `${childUnit.members[1].name}'s parent col ${member2Col})`
                  );
                } else if (member1Col !== null) {
                  // Use same column as first member's parent or adjacent
                  targetColumn = usedColumns.has(member1Col) ? member1Col + 1 : member1Col;
                } else if (member2Col !== null) {
                  targetColumn = usedColumns.has(member2Col) ? member2Col + 1 : member2Col;
                } else {
                  // Fallback to parent unit column
                  targetColumn = parentUnit.column;
                }
              } else {
                // For non-couples, use exact same column as parent
                targetColumn = parentUnit.column;
              }

              // Find next available column if target is taken (check both local and global reservations)
              while (usedColumns.has(targetColumn) || globalUsedColumns.has(targetColumn)) {
                targetColumn++;
              }

              // Ensure we don't go negative
              targetColumn = Math.max(0, targetColumn);

              childUnit.column = targetColumn;
              childUnit.columnSpan = targetSpan;
              usedColumns.add(targetColumn);
              globalUsedColumns.add(targetColumn);

              // Verify adjacency to parent unit
              const parentMinCol = parentUnit.column;
              const parentMaxCol = parentUnit.column + parentUnit.columnSpan - 1;
              const isAdjacent =
                (childUnit.column <= parentMaxCol + 1) &&
                (childUnit.column + childUnit.columnSpan - 1 >= parentMinCol - 1);

              Logger.debug("GenogramLayout", 
                `    ${childUnit.members
                  .map((m) => m.name)
                  .join("+")} gets columns ${childUnit.column}-${
                  childUnit.column + childUnit.columnSpan - 1
                } (under ${parentUnit.members.map((m) => m.name).join("+")})` +
                (isAdjacent ? '' : ' [NOT ADJACENT!]')
              );
            });
          });

          // Bridge couples already pre-reserved; no further action here
        }
      }
    }
  }

  private positionNodes(
    generationUnits: Map<number, FamilyUnit[]>,
    totalColumns: number
  ): void {
    // Guard against empty diagrams
    if (totalColumns === 0 || generationUnits.size === 0) {
      Logger.debug("GenogramLayout", "  Empty diagram - skipping node positioning");
      return;
    }

    // Calculate actual column width based on drawing area
    const columnPixelWidth = Math.max(
      this.minColumnWidth,
      this.drawingWidth / totalColumns
    );

    Logger.debug("GenogramLayout", `  Drawing width: ${this.drawingWidth}px`);
    Logger.debug("GenogramLayout", `  Total columns: ${totalColumns}`);
    Logger.debug("GenogramLayout", `  Column width: ${columnPixelWidth.toFixed(1)}px per column`);

    generationUnits.forEach((units, gen) => {
      units.forEach((unit) => {
        const y = 100 + gen * this.generationGap;

        // Check if this is a married couple
        const isCouple =
          unit.members.length === 2 &&
          unit.members[0].spouses.includes(unit.members[1]);

        if (isCouple) {
          // Position couple together
          const columnCenter = unit.column + unit.columnSpan / 2;
          const centerX = columnCenter * columnPixelWidth;

          unit.members[0].x =
            centerX - (this.nodeWidth / 2 + this.spouseGap / 2);
          unit.members[1].x =
            centerX + (this.nodeWidth / 2 + this.spouseGap / 2);
          unit.members[0].y = y;
          unit.members[1].y = y;
        } else if (unit.members.length === 1) {
          // Single person - center in their column space
          const columnCenter = unit.column + unit.columnSpan / 2;
          const centerX = columnCenter * columnPixelWidth;

          unit.members[0].x = centerX;
          unit.members[0].y = y;

          // Mark if this is a single parent for visual distinction
          if (unit.isSingleParent) {
            unit.members[0].isSingleParent = true;
            Logger.debug("GenogramLayout", `  Positioned single parent: ${unit.members[0].name} at x=${centerX.toFixed(1)}, y=${y}`);
          }
        } else {
          // Multiple people (siblings) - distribute across their column span
          const startX = unit.column * columnPixelWidth;
          const endX = (unit.column + unit.columnSpan) * columnPixelWidth;
          const availableWidth = endX - startX;

          const n = unit.members.length;
          const totalGap = this.siblingGap * (n - 1);
          const totalNodesWidth = this.nodeWidth * n;
          const groupWidth = totalNodesWidth + totalGap;

          // Center the group within the available width
          const groupStartX = startX + (availableWidth - groupWidth) / 2;

          unit.members.forEach((member, idx) => {
            member.x =
              groupStartX +
              idx * (this.nodeWidth + this.siblingGap) +
              this.nodeWidth / 2;
          });

          // Set Y for all siblings
          unit.members.forEach((member) => {
            member.y = y;
          });
        }

        // Store column info for visualization
        unit.members.forEach((member) => {
          member.column = unit.column;
          member.columnSpan = unit.columnSpan;
          member.columnPixelWidth = columnPixelWidth;
          member.totalColumns = totalColumns;
        });
      });
    });
  }
}

const draw = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  nodes: GenogramPersonNode[]
): GenogramConnection[] => {
  const connections: GenogramConnection[] = [];

  ctx.fillStyle = "#fafafa";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(pan.x, pan.y);
  ctx.scale(zoom, zoom);

  if (showGrid && nodes.length > 0) {
    const firstNode = nodes[0];
    const totalColumns = firstNode.totalColumns ?? 10;
    const columnPixelWidth = firstNode.columnPixelWidth ?? 150;

    for (let col = 0; col <= totalColumns; col++) {
      const x = col * columnPixelWidth;
      ctx.strokeStyle = "rgba(200, 200, 200, 1)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 600);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    const generations = new Map<number, GenogramPersonNode[]>();
    nodes.forEach((node) => {
      if (!generations.has(node.generation)) {
        generations.set(node.generation, []);
      }
      generations.get(node.generation)!.push(node);
    });

    generations.forEach((genNodes, gen) => {
      const y = 100 + gen * 170;
      const unitMap = new Map<
        string,
        { unit: FamilyUnit; members: GenogramPersonNode[] }
      >();

      genNodes.forEach((node) => {
        const unitsForNode =
          node.familyUnits.length > 0
            ? node.familyUnits
            : node.familyUnit
            ? [node.familyUnit]
            : [];

        unitsForNode.forEach((familyUnit) => {
          const key = familyUnit.id;
          if (!unitMap.has(key)) {
            unitMap.set(key, { unit: familyUnit, members: [] });
          }
          unitMap.get(key)!.members.push(node);
        });
      });

      unitMap.forEach(({ unit, members }) => {
        const startX = unit.column * columnPixelWidth;
        const width = unit.columnSpan * columnPixelWidth;

        ctx.fillStyle = `hsla(${gen * 60 + 200}, 40%, 85%, 1)`;
        ctx.fillRect(startX, y - 50, width, 100);

        ctx.fillStyle = `hsla(${gen * 60 + 200}, 40%, 30%, 1)`;
        ctx.font = "10px monospace";
        ctx.textAlign = "center";
        const names = members
          .map((member) => member.name.split(" ")[0] ?? member.name)
          .join("+");
        ctx.fillText(
          `Cols ${unit.column}-${unit.column + unit.columnSpan - 1}`,
          startX + width / 2,
          y - 55
        );
        ctx.fillText(names, startX + width / 2, y + 65);
      });
    });
  }

  const drawnUnits = new Set<string>();
  const familyUnits = new Map<string, RenderFamilyUnit>();

  nodes.forEach((node) => {
    const unitsForNode =
      node.familyUnits.length > 0
        ? node.familyUnits
        : node.familyUnit
        ? [node.familyUnit]
        : [];

    unitsForNode.forEach((unit) => {
      const unitId = unit.id;
      if (!familyUnits.has(unitId)) {
        familyUnits.set(unitId, {
          members: [],
          children: [],
          unit,
        });
      }

      const entry = familyUnits.get(unitId)!;
      if (!entry.members.includes(node)) {
        entry.members.push(node);
      }
    });
  });

  familyUnits.forEach((entry) => {
    entry.children = entry.unit.children ?? [];
  });

  familyUnits.forEach((entry, unitId) => {
    const unitMembers = entry.members;
    if (unitMembers.length === 0) {
      return;
    }

    const primaryMember = unitMembers[0];
    const secondaryMember = unitMembers.length > 1 ? unitMembers[1] : null;
    const hasSpouse =
      secondaryMember !== null &&
      primaryMember.spouses.some((spouse) => spouse.id === secondaryMember.id);

    if (entry.children.length > 0 && !drawnUnits.has(unitId)) {
      drawnUnits.add(unitId);

      ctx.strokeStyle = "#666";
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      const parentXs: number[] =
        secondaryMember !== null
          ? [primaryMember.x, secondaryMember.x]
          : [primaryMember.x];
      const parentY = primaryMember.y;

      const parentConnector: GenogramConnection = {
        id: unitId,
        from: primaryMember.id,
        to: (secondaryMember ?? primaryMember).id,
        type: "parent",
        points: [],
      };

      if (secondaryMember !== null) {
        parentConnector.points.push(
          [parentXs[0], parentY + 25],
          [parentXs[1], parentY + 25]
        );
      } else {
        parentConnector.points.push(
          [parentXs[0], parentY + 25],
          [parentXs[0] + 50, parentY + 25]
        );
      }

      parentXs.forEach((x) => {
        ctx.moveTo(x, parentY + 25);
        ctx.lineTo(x, parentY + 50);
      });

      const busLeft = Math.min(...parentXs);
      const busRight = Math.max(...parentXs);
      const busY = parentY + 50;
      ctx.moveTo(busLeft, busY);
      ctx.lineTo(busRight, busY);
      parentConnector.points.push([busLeft, busY], [busRight, busY]);
      connections.push(parentConnector);

      const busCenter = (busLeft + busRight) / 2;

      entry.children.forEach((child, idx) => {
        const childTopY = child.y - 25;
        const midY = (childTopY + busY) / 2;

        ctx.moveTo(child.x, childTopY);
        ctx.lineTo(child.x, midY);
        ctx.lineTo(busCenter, midY);
        ctx.lineTo(busCenter, busY);

        const childConnection: GenogramConnection = {
          id: `${unitId}-child${idx}`,
          from: child.id,
          to: unitId,
          type: "child",
          points: [
            [child.x + NODE_WIDTH / 2, child.y],
            [child.x + NODE_WIDTH / 2, childTopY],
            [busCenter + NODE_WIDTH / 2, childTopY],
            [busCenter + NODE_WIDTH / 2, midY],
          ],
        };
        connections.push(childConnection);
      });

      ctx.stroke();
    } else if (hasSpouse && !drawnUnits.has(unitId)) {
      if (entry.children.length === 0) {
        drawnUnits.add(unitId);

        ctx.strokeStyle = "#666";
        ctx.lineWidth = 1.5;
        ctx.beginPath();

        const parentXs: number[] =
          secondaryMember !== null
            ? [primaryMember.x, secondaryMember.x]
            : [primaryMember.x];
        const parentY = primaryMember.y - 50;

        const relationshipConnection: GenogramConnection = {
          id: unitId,
          from: primaryMember.id,
          to: (secondaryMember ?? primaryMember).id,
          type: "relationship",
          points: [],
        };

        const busLeft = Math.min(...parentXs);
        const busRight = Math.max(...parentXs);
        const busY = parentY + 50;
        ctx.moveTo(busLeft, busY);
        ctx.lineTo(busRight, busY);

        const relationshipPoints: ConnectionPoint[] = [
          [busLeft + NODE_WIDTH, busY + NODE_HEIGHT / 2],
          [busRight, busY + NODE_HEIGHT / 2],
        ];

        connections.push({
          ...relationshipConnection,
          points: relationshipPoints,
        });

        ctx.stroke();
      }
    }
  });

  nodes.forEach((person) => {
    const isHovered = hoveredPerson === person.id;
    const isDeceased = person.name.includes("†");

    if (isHovered) {
      ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
    }

    if (person.gender === "M") {
      ctx.fillStyle = isDeceased ? "#d1d5db" : "#dbeafe";
      ctx.strokeStyle = isHovered ? "#1e40af" : "#3b82f6";
      ctx.lineWidth = 2;
      ctx.fillRect(person.x - 35, person.y - 25, 70, 50);
      ctx.strokeRect(person.x - 35, person.y - 25, 70, 50);
    } else {
      ctx.fillStyle = isDeceased ? "#d1d5db" : "#fce7f3";
      ctx.strokeStyle = isHovered ? "#be185d" : "#ec4899";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(person.x, person.y, 28, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;

    ctx.fillStyle = "#111827";
    ctx.font = "11px system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const nameParts = person.name.split(" ");
    if (nameParts.length === 1 || person.name.length < 10) {
      ctx.fillText(person.name, person.x, person.y);
    } else {
      ctx.fillText(nameParts[0], person.x, person.y - 6);
      ctx.fillText(nameParts.slice(1).join(" "), person.x, person.y + 6);
    }
  });

  ctx.restore();
  return connections;
};

const resolveGenogramLayoutOptionsForFamily = (
  familyData: FamilyData,
  drawingWidth?: number
): LayoutOptions => {
  const personManifest = getShapeManifest("genogram", "person");
  const personSize = Math.max(
    1,
    Number(personManifest?.defaultDimensions?.width) || 100
  );
  const horizontalSpacing = Math.max(260, personSize * 4);
  const coupleHorizontalOffset = Math.max(180, horizontalSpacing * 0.65);
  const spouseGap = 2 * coupleHorizontalOffset - personSize;
  const maxNameLen = (familyData.people ?? []).reduce((max, p) => {
    const n = typeof p.name === "string" ? p.name.length : 0;
    return Math.max(max, n);
  }, 0);
  const nameAwareMinColumn = Math.min(320, Math.max(150, 72 + maxNameLen * 7));
  return {
    drawingWidth:
      drawingWidth ??
      (typeof window !== "undefined" ? window.innerWidth - 100 : 1800),
    nodeWidth: personSize,
    spouseGap,
    siblingGap: Math.max(90, Math.round(personSize * 0.85)),
    generationGap: Math.max(170, Math.round(personSize * 1.75)),
    minColumnWidth: nameAwareMinColumn,
  };
};

const run = (tree: FamilyData): LayoutResult => {
  const canvasSize = { width: 4000, height: 4000 };
  const layout = new GenogramLayoutService(
    resolveGenogramLayoutOptionsForFamily(tree, canvasSize.width - 100)
  );

  const layoutNodes = layout.layout(tree);

  if (layoutNodes.length === 0) {
    return { nodes: [], edges: [] };
  }

  const canvasElement = document.getElementById("genogramCanvas");
  const canvas =
    canvasElement instanceof HTMLCanvasElement ? canvasElement : null;
  let layoutConnections: GenogramConnection[] = [];

  if (canvas) {
    const ctx = canvas.getContext("2d");
    if (ctx) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      layoutConnections = draw(canvas, ctx, layoutNodes);
    }
  }

  return {
    nodes: layoutNodes.map((node) => ({
      id: node.id,
      name: node.name,
      gender: node.gender,
      x: node.x,
      y: node.y,
      generation: node.generation,
      siblings: node.siblings.map((sibling) => sibling.id),
      spouses: node.spouses.map((spouse) => spouse.id),
      culture: node.culture,
      sexualOrientation: node.sexualOrientation,
      medicalConditions: node.medicalConditions,
    })),
    edges: layoutConnections,
  };
};

export { GenogramLayoutService, resolveGenogramLayoutOptionsForFamily, run };
export type {
  FamilyData,
  FamilyPerson,
  GenogramConnection,
  GenogramPersonNode,
  LayoutOptions,
  LayoutResult,
  ParentChildRelation,
};
